
import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { motion } from "framer-motion";
import { CheckCircle2, ArrowRight } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { OrderDetails } from "@/lib/orderTypes"; // Assuming OrderDetails type is compatible or can be adjusted
import { toast } from "sonner";
import { api } from '@/components/client/backend';

declare global {
  interface Window {
    gtag: (...args: unknown[]) => void;
  }
}

const OrderConfirmation = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const success = searchParams.get("success");
  const sessionId = searchParams.get("session_id");

  const { data: orderDetails, isLoading } = useQuery<OrderDetails | null>({
    queryKey: ["orderDetails", sessionId],
    queryFn: async () => {
      if (!sessionId) {
        console.error("No session ID provided");
        return null;
      }

      try {
        // Fetch order details using ky
        const data = await api('orders', {
          searchParams: { session_id: sessionId.trim() }
        }).json<OrderDetails>();

        return data;
      } catch (error) {
        console.error("Error fetching order details:", error);
        // Handle specific HTTP errors if needed
        // if (error instanceof HTTPError) { ... }
        toast.error("Unable to load order details. Please try again later.");
        return null;
      }
    },
    enabled: !!sessionId && success === "true",
    retry: 3,
    retryDelay: 1000,
    staleTime: 0,
    gcTime: 0
  });

  useEffect(() => {
    if (success !== "true") {
      navigate("/");
    }
  }, [success, navigate]);

  useEffect(() => {
    // Google Ads Conversion Tracking
    if (success === "true" && orderDetails) {
      try {
        window.gtag?.('event', 'conversion', {
          'send_to': 'AW-16856634107/Hdz9CJ_wspsaEPul7-U-',
          'value': orderDetails.total_amount,
          'currency': 'AUD',
          'transaction_id': orderDetails.reference
        });
        console.log('Conversion event sent to Google Ads');
      } catch (error) {
        console.error('Error sending conversion event:', error);
      }
    }
  }, [success, orderDetails]);

  if (success !== "true") {
    return null;
  }

  const formatTicketSummary = (order: OrderDetails) => {
    const parts = [];
    if (order.vip_seats?.length) {
      parts.push(`VIP Seats: ${order.vip_seats.join(", ")}`);
    }
    if (order.standing_tickets) {
      parts.push(`General Admission (${order.standing_tickets})`);
    }
    if (order.early_bird_tickets) {
      parts.push(`Early Bird (${order.early_bird_tickets})`);
    }
    return parts.join(", ");
  };

  return (
    <div className="min-h-screen bg-black flex flex-col">
      <Header />
      <main className="flex-grow container mx-auto px-4 py-32">
        <div className="max-w-2xl mx-auto text-center space-y-8">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ duration: 0.5 }}
            className="inline-block"
          >
            <CheckCircle2 className="w-16 h-16 md:w-24 md:h-24 text-green-500 mx-auto" />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="space-y-4"
          >
            <h1 className="text-2xl md:text-4xl font-bold text-white">
              Success! Your Order is Confirmed
            </h1>
            <p className="text-base md:text-lg text-gray-300">
              Thank you for your purchase! Your tickets are now secured.
            </p>
          </motion.div>

          {isLoading ? (
            <div className="space-y-4 bg-gray-800/50 rounded-lg p-6">
              <Skeleton className="h-6 w-3/4 mx-auto" />
              <Skeleton className="h-6 w-1/2 mx-auto" />
              <Skeleton className="h-6 w-2/3 mx-auto" />
            </div>
          ) : orderDetails ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-gray-800/50 rounded-lg p-6 text-left space-y-4"
            >
              <div>
                <h3 className="text-sm text-gray-400">Order Reference</h3>
                <p className="text-white font-semibold">{orderDetails.reference}</p>
              </div>
              <div>
                <h3 className="text-sm text-gray-400">Customer</h3>
                <p className="text-white">
                  {orderDetails.user_first_name} {orderDetails.user_last_name}
                </p>
                <p className="text-gray-400">{orderDetails.user_email}</p>
              </div>
              <div>
                <h3 className="text-sm text-gray-400">Tickets</h3>
                <p className="text-white">{formatTicketSummary(orderDetails)}</p>
              </div>
              <div>
                <h3 className="text-sm text-gray-400">Total Paid</h3>
                <p className="text-white font-semibold">
                  AUD ${orderDetails.total_amount.toFixed(2)}
                </p>
              </div>
            </motion.div>
          ) : null}

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="space-y-6"
          >
            <p className="text-gray-300 text-sm md:text-base">
              We will send a confirmation email shortly. If you don't see it in your inbox, please check your junk or spam folder.
            </p>

            <Button
              onClick={() => navigate("/")}
              className="space-x-2 bg-[#9b51e0] hover:bg-[#843fc5] transition-colors duration-300 
                       shadow-[0_0_15px_rgba(155,81,224,0.3)] hover:shadow-[0_0_20px_rgba(155,81,224,0.5)]
                       text-white font-medium w-full md:w-auto animate-button-glow"
            >
              <span>Continue Browsing</span>
              <ArrowRight className="w-4 h-4" />
            </Button>
          </motion.div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default OrderConfirmation;
